import React, { useEffect, useState, useRef } from "react";
import {
  CButton,
  CNavLink,
  CNav,
  CTabPane,
  CNavItem,
  CTabContent,
  CFormInput,
  CToast,
  CToastHeader,
  CToastBody,
  CToaster,
  CRow,
  CCol,
  CCardImage,
} from "@coreui/react";
import { useParams } from "react-router";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

import _, { uniqueId } from "lodash";
import API_SERVICES from "../../services";
import {
  StaticPageState,
  UpdateContentFunction,
} from "../../models/staticmenu.models";
import commonService from "../../services/common.service";
import handleResponse from "../../services/apiConfig/api.response.service";
import {
  getChannelSubmenu,
  getMediaTypeChannel,
} from "../../services/channel.service";
import { processChannelSubmenuResponse } from "./channelSubmenu.utils";
import { CHANNEL_SUBMENU_SAMPLE } from "./channelSubmenuSample.data";
import { CHANNEL_IMAGE_SAMPLE } from "./channelImageSample.data";
import {
  MediaObject,
  MediaOriginalDataProps,
  TitleError,
} from "../../models/channel.model";
import PageLoader from "../../components/PageLoader/PageLoader";
import { useNavigate } from "react-router-dom";
import {
  COMMON_CONSTANTS,
  MEDIA_TYPES,
  POINT_CONSTANTS,
} from "../../constants/commonConstants";
import { ToastContent, getPageContent } from "../../utils/editor.utils";
import MyEditor from "../../components/Editor/Editor";
import { CONSTANTS } from "../../constants";
import MediaCard from "../../components/MediaCard/MediaCard";
import Modal from "../../components/coreui/Modal";
import InteractiveImageModal from "../../components/InteractiveImageModal/InteractiveImageModal";

const EditChannel: React.FC = () => {
  const [activeKey, setActiveKey] = useState(1);
  const [activeMedia, setActiveMedia] = useState<MediaObject | null>();
  const [allMediaData, setAllMediaData] =
    useState<MediaOriginalDataProps | null>();
  const [errors, setErrors] = useState<TitleError>({});
  const [editorId, setEditorId] = useState(uniqueId);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpenToaster, setIsOpenToaster] = useState(false);
  const [isTitleValid, setIsTitleValid] = useState(true);
  const [isTextValid, setIsTextValid] = useState(true);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [language, setLanguage] = useState("en");
  const [pointImageFlag, setPointImageFlag] = useState<boolean>(false);
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [staticPage, setStaticPage] = useState<StaticPageState>({
    en: {
      title: "",
      text: "",
    },
    de: {
      title: "",
      text: "",
    },
    es: {
      title: "",
      text: "",
    },
    ru: {
      title: "",
      text: "",
    },
  });
  const [textMessage, setTextMessage] = useState("");
  const [titleMessage, setTitleMessage] = useState("");
  const [toast, showToast] = useState<string | React.ReactNode>(0);
  const toaster = useRef<HTMLDivElement | null>(null);

  const navigate = useNavigate();
  const params = useParams<{ uuid: string }>();

  useEffect(() => {
    fetchChannelDetails();
    fetchAllMediaType();
  }, []);

  useEffect(() => {
    if (isOpenToaster) {
      ToastContent(isOpenToaster, errors);
      setIsOpenToaster(false);
    }
  }, [errors]);

  const handleContentChange: UpdateContentFunction = (htmlContent: string) => {
    setStaticPage(prevStaticPage => {
      const list: any = _.cloneDeep(prevStaticPage);
      list[selectedLanguage] = { ...list[selectedLanguage], text: htmlContent };
      return list;
    });
    setIsTextValid(htmlContent.trim() !== "");
    if (htmlContent.trim() === "") {
      setTextMessage("Text is required");
    } else {
      setTextMessage("");
    }
  };

  const closeVideoModal = () => {
    setIsVideoModalOpen(false);
    setActiveMedia(null);
  };

  const fetchAllMediaType = async () => {
    const image = await getMediaTypeChannel("channel_image", MEDIA_TYPES.IMAGE);
    const images = image.data;
    // images = CHANNEL_IMAGE_SAMPLE; // TODO: remove when API is ready
    setAllMediaData({
      image: images,
    });
  };

  const handleSave = () => {
    setIsLoading(true);
    const test = getPageContent(staticPage);

    if (Object.keys(test).length > 0) {
      setErrors(test);
      setIsOpenToaster(true);
      setIsLoading(false);
    } else {
      const locales: Record<string, any> = {};
      Object.keys(staticPage).forEach(langCode => {
        locales[langCode] = {
          title: staticPage[langCode].title,
          text: staticPage[langCode].text,
          channel_image: staticPage[langCode].channel_image,
        };
      });
      const payload = {
        page_id: params.uuid as string,
        locales,
      };
      API_SERVICES.CHANNEL.SAVE_CHANNEL_DETAILS(payload).then(data => {
        const responseData = handleResponse(data);
        if (responseData.ok) {
          commonService.toastService(responseData.data, "success");
          fetchChannelDetails();
        } else {
          commonService.toastService("Failed to update channel menu", "error");
        }
        setIsLoading(false);
      });
    }
  };

  const openVideoModal = (selectedActiveMedia: MediaObject | undefined) => {
    setIsVideoModalOpen(true);
    setActiveMedia(selectedActiveMedia);
  };

  const updateFlag = () => {
    fetchChannelDetails();
  };

  const handleDeleteChannelPoint = (id: number) => {
    API_SERVICES.CHANNEL.DELETE_CHANNEL_INTERACTIVE_POINT(id).then(data => {
      const responseData = handleResponse(data);
      if (responseData.ok) {
        commonService.toastService(responseData.data, "success");
        updateFlag();
      } else {
        commonService.toastService("Failed to delete point", "error");
      }
    });
  };

  const handleAddChannelPoint = (newPoint: any[]) => {
    const point = newPoint[0];
    const channelImageId = activeMedia?.media_id;
    if (!channelImageId) {
      commonService.toastService("Channel image not selected", "error");
      return;
    }
    const payload = {
      pos_x: point.pos_x,
      pos_y: point.pos_y,
      point_id: point.point_id,
      channel_image_id: channelImageId,
    };
    API_SERVICES.CHANNEL.SAVE_CHANNEL_INTERACTIVE_POINT(payload).then(data => {
      const responseData = handleResponse(data);
      if (responseData.ok) {
        commonService.toastService(responseData.data, "success");
        updateFlag();
      } else {
        commonService.toastService("Failed to save point", "error");
      }
    });
  };

  const updateStaticPageMedia = (data: MediaObject, mediaType: string) => {
    setPointImageFlag(false);
    setStaticPage(prevStaticPage => {
      const list: any = _.cloneDeep(prevStaticPage);

      if (!list[language]) {
        list[language] = { title: "", text: "" };
      }

      if (mediaType === MEDIA_TYPES.IMAGE) {
        const previousMediaId = list[language].image?.[0]?.media_id;
        const isNewImage = previousMediaId !== data.media_id;

        list[language].image = [{ ...data, point_media_id: null }];
        list[language].channel_image = data.media_id ?? null;

        if (isNewImage) {
          list[language].point_mapping = [];
        }
      }

      return list;
    });
  };

  const handleCancel = () => {
    setStaticPage(staticPage);
    setIsTitleValid(true);
    setIsTextValid(true);
    setTitleMessage("");
    setTextMessage("");
    navigate(COMMON_CONSTANTS.PREVIOUS_PAGE);
  };

  const handleTitleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    language: keyof StaticPageState
  ) => {
    const newTitle = event.target.value;

    setStaticPage(prevState => ({
      ...prevState,
      [language]: {
        ...prevState[language],
        title: newTitle,
      },
    }));
    selectedLanguage === "en" && setIsTitleValid(newTitle.trim() !== "");
    if (newTitle.trim() === "") {
      selectedLanguage === "en" && setTitleMessage("English title is required");
    } else {
      setTitleMessage("");
    }
  };
  const fetchChannelDetails = async () => {
    const data = await getChannelSubmenu(params?.uuid as string);
    const responseData = data.data;
    // responseData = CHANNEL_SUBMENU_SAMPLE; // TODO: remove when API returns channel_image and point_mapping
    const transformedData = processChannelSubmenuResponse(responseData);
    setPointImageFlag(true);
    setEditorId(uniqueId);
    setStaticPage(prevStaticPage => {
      const list = _.cloneDeep(prevStaticPage);

      Object.keys(transformedData).forEach((element: string) => {
        list[element] = transformedData[element]
          ? { ...list[element], ...transformedData[element] }
          : list[element];
      });
      return list;
    });
  };

  const activeMediaProps = {
    title:
      _.capitalize(activeMedia?.media_type) +
      " - " +
      _.capitalize(activeMedia?.title || ""),
    type: activeMedia?.media_type,
  };

  const updateContent: UpdateContentFunction = newContent => {
    setStaticPage(prevStaticPage => {
      const list: any = _.cloneDeep(prevStaticPage);
      list[language].text = newContent;
      return list;
    });
    setIsTextValid(newContent.trim() !== "");
    if (newContent.trim() === "") {
      setTextMessage("Text is required");
    } else {
      setTextMessage("");
    }
  };

  return (
    <>
      <CNav variant="tabs" role="tablist">
        <CNavItem role="presentation">
          <CNavLink
            active={activeKey === CONSTANTS.ACTIVE_KEY.EN}
            component="button"
            role="tab"
            aria-controls="home-tab-pane"
            aria-selected={activeKey === CONSTANTS.ACTIVE_KEY.EN}
            onClick={() => {
              setLanguage("en");
              setSelectedLanguage("en");
              setActiveKey(CONSTANTS.ACTIVE_KEY.EN);
            }}
          >
            English
          </CNavLink>
        </CNavItem>

        <CNavItem role="presentation">
          <CNavLink
            active={activeKey === CONSTANTS.ACTIVE_KEY.DE}
            component="button"
            role="tab"
            aria-controls="profile-tab-pane"
            aria-selected={activeKey === CONSTANTS.ACTIVE_KEY.DE}
            onClick={() => {
              setLanguage("de");
              setSelectedLanguage("de");
              setActiveKey(CONSTANTS.ACTIVE_KEY.DE);
            }}
          >
            German
          </CNavLink>
        </CNavItem>

        <CNavItem role="presentation">
          <CNavLink
            active={activeKey === CONSTANTS.ACTIVE_KEY.ES}
            component="button"
            role="tab"
            aria-controls="contact-tab-pane"
            aria-selected={activeKey === CONSTANTS.ACTIVE_KEY.ES}
            onClick={() => {
              setLanguage("es");
              setSelectedLanguage("es");
              setActiveKey(CONSTANTS.ACTIVE_KEY.ES);
            }}
          >
            Spanish
          </CNavLink>
        </CNavItem>

        <CNavItem role="presentation">
          <CNavLink
            active={activeKey === CONSTANTS.ACTIVE_KEY.RU}
            component="button"
            role="tab"
            aria-controls="contact-tab-pane"
            aria-selected={activeKey === CONSTANTS.ACTIVE_KEY.RU}
            onClick={() => {
              setLanguage("ru");
              setSelectedLanguage("ru");
              setActiveKey(CONSTANTS.ACTIVE_KEY.RU);
            }}
          >
            Russian
          </CNavLink>
        </CNavItem>
      </CNav>
      <CToaster
        ref={toaster}
        push={toast as React.ReactElement<string>}
        placement="bottom-center"
      />
      <CTabContent>
        {Object.keys(staticPage).map(
          (element: keyof StaticPageState, index) => {
            return (
              <CTabPane
                key={index}
                role="tabpanel"
                aria-labelledby="home-tab-pane"
                visible={activeKey === Number(index) + 1}
              >
                <CFormInput
                  className="form-title"
                  type="text"
                  value={staticPage[element as keyof StaticPageState].title}
                  onChange={e =>
                    handleTitleChange(e, language as keyof StaticPageState)
                  }
                />
                {titleMessage && (
                  <div className="error-message">{titleMessage}</div>
                )}{" "}
                {staticPage ? (
                  <MyEditor
                    key={language + editorId}
                    updateContent={updateContent}
                    initialContent={
                      staticPage[element as keyof StaticPageState]
                        ? staticPage[element as keyof StaticPageState].text
                        : ""
                    }
                  />
                ) : (
                  <MyEditor
                    key="data2"
                    initialContent={""}
                    updateContent={updateContent}
                  />
                )}
                {textMessage && (
                  <div className="error-message">{textMessage}</div>
                )}{" "}
              </CTabPane>
            );
          }
        )}
      </CTabContent>
      <CRow className="mt-3">
        <CCol sm={6}>
          <MediaCard
            key={language}
            mediaData={allMediaData?.image}
            cardTitle="Image"
            type="image"
            mediaArray={staticPage[language]?.image}
            onClickImage={openVideoModal}
            updateData={(data: MediaObject) => {
              updateStaticPageMedia(data, "image");
            }}
          />
        </CCol>
      </CRow>
      <div className="mp-button-container">
        <CButton className="me-2" color="secondary" onClick={handleCancel}>
          Cancel
        </CButton>
        <CButton
          color="danger"
          onClick={handleSave}
          disabled={!isTitleValid || !isTextValid}
        >
          Save
        </CButton>
      </div>
      <Modal
        className="modal-static"
        onSave={handleSave}
        activeMediaProps={activeMediaProps}
        onClose={closeVideoModal}
        open={isVideoModalOpen}
        showSave={false}
      >
        {activeMedia?.media_type !== POINT_CONSTANTS.CHINESE_IMAGE ? (
          <InteractiveImageModal
            key={activeMedia?.media_id}
            interactiveImagePoints={staticPage[language]?.point_mapping}
            interactiveImage={activeMedia}
            onAddPoint={handleAddChannelPoint}
            onDeletePoint={handleDeleteChannelPoint}
            pointImageFlag={pointImageFlag}
            updateFlag={updateFlag}
          />
        ) : (
          <CCardImage orientation="top" src={activeMedia?.file_name} />
        )}
      </Modal>
      <PageLoader isLoading={isLoading} overlayOnly={false} />
    </>
  );
};

export default EditChannel;
